import type { ChartData } from 'chart.js'
import _round from 'lodash/round'

import SubscriptionIcon from '@/assets/icons/icon-subscriptions.svg'
import TipsIcon from '@/assets/icons/icon-tips.svg'
import MessagesIcon from '@/assets/icons/icons-messages.svg'
import { DoughnutChart } from '@/components/charts/doughnut-chart.tsx'
import { Card, CardContent } from '@/components/ui/card.tsx'
import { Skeleton } from '@/components/ui/skeleton.tsx'
import { useCustomizableValue } from '@/modules/sales-editor/hooks/use-customizable-value.ts'

import type { CategoryChartType } from '../../types'
import { CategoryBox } from './CategoryBox'
import { buildCategoryChartCacheKey } from './utils.ts'

const mapData = (data: Array<CategoryChartType>) => {
  const res: ChartData<'doughnut'> = {
    labels: data.map((item) => item.title),
    datasets: [
      {
        data: data.map((item) => item.value),
        backgroundColor: data.map((item) => item.color),
      },
    ],
  }

  return res
}

interface Props {
  subscriptionsRevenue: number
  messagesRevenue: number
  tipsRevenue: number
  isFetching: boolean
  showCents?: boolean
  dateRange: string
}

export const CategoryChart = ({
  subscriptionsRevenue,
  messagesRevenue,
  tipsRevenue,
  isFetching,
  showCents,
  dateRange,
}: Props) => {
  const customizableSubscriptions = useCustomizableValue({
    originalValue: subscriptionsRevenue,
    name: buildCategoryChartCacheKey('subscriptions'),
    dateRange,
  })
  const customizableMessages = useCustomizableValue({
    originalValue: messagesRevenue,
    name: buildCategoryChartCacheKey('messages'),
    dateRange,
  })
  const customizableTips = useCustomizableValue({
    originalValue: tipsRevenue,
    name: buildCategoryChartCacheKey('tips'),
    dateRange,
  })

  const data: Array<CategoryChartType> = [
    {
      title: 'Subscriptions',
      tooltip:
        'Shows the total number of active and newly acquired subscriptions within the selected timeframe, providing insight into subscriber growth trends.',
      value: _round(customizableSubscriptions.value, showCents ? 2 : 0),
      color: '#EF358E',
      icon: <SubscriptionIcon />,
      onChange: customizableSubscriptions.onChange,
      testId: 'category-box-subscriptions',
    },
    {
      title: 'Messages',
      tooltip:
        'Shows the total revenue generated from paid messages and media unlocks, reflecting fan engagement through content purchases.',
      value: _round(customizableMessages.value, showCents ? 2 : 0),
      color: '#9601CA',
      icon: <MessagesIcon />,
      onChange: customizableMessages.onChange,
      testId: 'category-box-messages',
    },
    {
      title: 'Tips',
      tooltip:
        'Shows the total income earned from fan tips, including post tips and all other forms of tips.',
      value: _round(customizableTips.value, showCents ? 2 : 0),
      color: '#0BC9CD',
      icon: <TipsIcon />,
      onChange: customizableTips.onChange,
      testId: 'category-box-tips',
    },
  ]

  if (isFetching) {
    return (
      <Card className="flex flex-col justify-between">
        <CardContent className="grid h-full grid-flow-col grid-cols-2 justify-center p-6">
          <Skeleton className="mx-auto h-[200px] w-[200px] rounded-full" />
          <div className="grid grid-flow-row gap-3">
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="flex flex-col justify-between">
      <CardContent className="grid h-full grid-flow-col grid-cols-2 content-center justify-center gap-6 p-6 sm:gap-0">
        <div className="flex h-full w-full items-center justify-center sm:pr-6">
          {customizableTips.value +
            customizableMessages.value +
            customizableSubscriptions.value ===
          0 ? (
            <div className="border-background h-32 w-32  rounded-full border-[30px] sm:h-52 sm:w-52 sm:border-[40px]" />
          ) : (
            <DoughnutChart
              data={mapData(data)}
              toggleOptions={{ isTooltipCurrency: true, hideLegend: true }}
              showCents={showCents}
            />
          )}
        </div>
        <div className="flex flex-col items-stretch justify-center gap-3">
          {data.map((item) => {
            return (
              <CategoryBox
                key={item.title}
                data={item}
                showCents={showCents}
              />
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
